import { getAnalyticsClient } from '@carvana/analytics';
import SafeBaseClient from './safe-base-client';
import type { EventData, TrackFn } from './safe-base-client';

interface DataLayer {
  push: (payload: EventData) => void;
}

/**
 * `|/scripts/gtm-config` runs the GTM snippet at bootstrap in both entry points, so
 * window.dataLayer already exists by the time anything tracks. getAnalyticsClient
 * only fetches the url when the global is missing, which makes the url below a
 * fallback that does not fire in practice — it stays so this client still works if
 * the snippet is ever removed.
 *
 * getAnalyticsClient is untyped upstream and may hand back the global, a promise of
 * it, or undefined; this is the one place that shape is asserted.
 */
const getClient = async (id?: string): Promise<DataLayer | undefined> => {
  const client = (await getAnalyticsClient(
    'dataLayer',
    `https://www.googletagmanager.com/gtm.js?id=${id}`
  )) as DataLayer | null | undefined;

  return client ?? undefined;
};

export default class GoogleTagManagerClient extends SafeBaseClient {
  constructor(config: { id?: string }) {
    super('gtm', config);
  }

  trackEvent: TrackFn = async (eventName, eventData = {}, log, noPublish) => {
    const dataLayer = await getClient(this.config.id as string | undefined);
    if (!dataLayer) return;

    if (log) log('[Analytics_GoogleTagManager_Event]', { eventName, eventData });
    if (!noPublish) dataLayer.push({ ...eventData });
  };

  trackUser: TrackFn = async (eventName, eventData = {}, log, noPublish) => {
    const dataLayer = await getClient(this.config.id as string | undefined);
    if (!dataLayer) return;

    if (log) log('[Analytics_GoogleTagManager_User]', { eventName, eventData });
    if (!noPublish) dataLayer.push({ uid: eventData.userId });
  };
}
